import React, { useCallback } from "react";
import { View, Text } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
// import { ResumePDFText } from "components/Resume/ResumePDF/common";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import { handleClick } from "lib/utils/navigator";
import { useFontSize } from "components/Resume/ResumePDF/FontSizeContext";

export const TemplateSixPDFSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  return (
    <div onClick={handleClickCallback} style={{ cursor: "pointer" }}>
      <View
        wrap={false}
        style={{
          ...styles.flexCol,
          marginBottom: spacing["2"], // Reduced for better page utilization
          marginLeft: spacing["4"], 
          marginRight: spacing["4"],
          marginTop: spacing["2"], // Reduced for better page utilization
          ...style,
        }}
      >
        {heading && (
          <View
            style={{
              width: "100%",
              borderBottom: `1.5pt solid ${themeColor || DEFAULT_FONT_COLOR}`,
              borderTop: `1.5pt solid ${themeColor || DEFAULT_FONT_COLOR}`,
              paddingBottom: spacing["1.5"],
              paddingTop: spacing["1.5"],
              marginBottom: spacing["1"], // Reduced gap after heading
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                ...dynamicStyles.heading,
                textAlign: "center",
                fontWeight: "bold",
                letterSpacing: "0.5pt", // Slightly more spacing for centered headers
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
          </View>
        )}
        <View style={{ width: "100%" }}>
          {children}
        </View>
      </View>
    </div>
  );
};

// subsections for  orphan heading prevention
export const TemplateSixPDFSectionWithSubsections = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  const childrenArray = React.Children.toArray(children);
  const firstChild = childrenArray[0];
  const restChildren = childrenArray.slice(1);
  
  return (
    <div onClick={handleClickCallback} style={{ cursor: "pointer" }}>
      {/* Wrap heading with first child to prevent orphan headings */}
      {heading && firstChild && (
        <View
          style={{
            ...styles.flexCol,
            marginBottom: spacing["1.5"],
            marginLeft: spacing["4"],
            marginRight: spacing["4"],
            marginTop: spacing["2"],
          }}
          wrap={false}
        >
          {/* Heading */}
          <View
            style={{
              width: "100%",
              borderBottom: `1.5pt solid ${themeColor || DEFAULT_FONT_COLOR}`,
              borderTop: `1.5pt solid ${themeColor || DEFAULT_FONT_COLOR}`,
              paddingBottom: spacing["1.5"],
              paddingTop: spacing["1.5"],
              marginBottom: spacing["1"],
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                ...dynamicStyles.heading,
                textAlign: "center",
                fontWeight: "bold",
                letterSpacing: "0.5pt",
                  color: themeColor || DEFAULT_FONT_COLOR,
                }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
          </View>
          {/* First child */}
          <View style={{ width: "100%" }}>
            {firstChild}
          </View>
        </View>
      )}
      
      {/* Heading only (when no children) */}
      {heading && !firstChild && (
        <View
          style={{
            ...styles.flexCol,
            marginBottom: spacing["2"],
            marginLeft: spacing["4"],
            marginRight: spacing["4"],
            marginTop: spacing["3"],
          }}
        >
          <View
            style={{
              width: "100%",
              borderBottom: `1.5pt solid ${themeColor || DEFAULT_FONT_COLOR}`,
              borderTop: `1.5pt solid ${themeColor || DEFAULT_FONT_COLOR}`,
              paddingBottom: spacing["1.5"],
              paddingTop: spacing["1.5"],
              marginBottom: spacing["1"],
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                ...dynamicStyles.heading,
                textAlign: "center",
                fontWeight: "bold",
                letterSpacing: "0.5pt",
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
          </View>
        </View>
      )}
      
      {/* Rest of children */}
      {restChildren.length > 0 && (
        <View
          style={{
            ...styles.flexCol,
            marginBottom: spacing["2"],
            marginLeft: spacing["4"],
            marginRight: spacing["4"],
            gap: spacing["0.5"], // Very tight gap for maximum content density
          }}
        >
          {restChildren}
        </View>
      )}
    </div>
  );
};

// Compact text component for better space utilization
export const TemplateSixPDFText = ({
  bold = false,
  themeColor,
  style = {},
  children,
}: {
  bold?: boolean;
  themeColor?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  return (
    <Text
      style={{
        ...dynamicStyles.text,
        color: themeColor || DEFAULT_FONT_COLOR,
        fontWeight: bold ? "bold" : "normal",
        lineHeight: "1.3", 
        ...style,
      }}
      debug={DEBUG_RESUME_PDF_FLAG}
    >
      {children}
    </Text>
  );
};

// Compact bullet point component for lists
export const TemplateSixPDFBulletList = ({
  themeColor,
  children,
}: {
  themeColor?: string;
  children: React.ReactNode;
}) => (
  <View
    style={{
      ...styles.flexCol,
      gap: spacing["0.5"], // Tight spacing for bullet lists
      marginLeft: spacing["2"],
    }}
  >
    {children}
  </View>
);

// Individual bullet item
export const TemplateSixPDFBulletItem = ({
  themeColor,
  children,
}: {
  themeColor?: string;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  return (
    <View
      style={{
        ...styles.flexRow,
        alignItems: "flex-start",
        gap: spacing["0.5"], // Reduced gap for tighter bullet items
      }}
    >
      <Text
        style={{
          ...dynamicStyles.text,
          color: themeColor || DEFAULT_FONT_COLOR,
          fontWeight: "bold",
          marginTop: "1pt",
        }}
      >
        •
      </Text>
      <View style={{ flex: 1 }}>
        <TemplateSixPDFText themeColor={themeColor}>
          {children}
        </TemplateSixPDFText>
      </View>
    </View>
  );
};